import { defineMessages, FormattedMessage } from "react-intl";

import { CheckboxField } from "@ps-design-system/components/Checkbox";
import {
  RadioGroupField,
  RadioGroupItem,
} from "@ps-design-system/components/RadioGroup";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";

import { ListingUseType } from "../../../../../../../components/src/services/listings.service";
import { USE_TYPE_SLUG } from "../../../ActivateListingsPage.consts";

import { InstantBookLearnMorePopover } from "./InstantBookLearnMorePopover";

type InstantBookingFieldsProps = {
  useType: ListingUseType;
  isInstantBookEligible: boolean;
};

const UPSELL_LISTING_NAME_BY_USE_TYPE = defineMessages({
  Event: {
    id: "hosting.activate_listings.instant_book_benefit_event",
    defaultMessage: "Event",
  },
  "Off-Site": {
    id: "hosting.activate_listings.instant_book_benefit_offsite",
    defaultMessage: "Meeting",
  },
  Production: {
    id: "hosting.activate_listings.instant_book_benefit_production",
    defaultMessage: "Production",
  },
});

/** The "Instant booking" section: eligible hosts choose who can book instantly; non-eligible hosts can request access. */
const InstantBookingFields = ({
  useType,
  isInstantBookEligible,
}: InstantBookingFieldsProps) => {
  const intl = useIntl();
  const slug = USE_TYPE_SLUG[useType];

  return (
    <div>
      <Text
        typeface="headline-2-m"
        element="p"
        className="tw-border-b tw-border-gray-300 tw-py-8 tw-mb-24"
        testId={`activate-listing-form-${slug}-instant-book-header`}
      >
        <FormattedMessage
          id="hosting.activate_listings.instant_booking_header"
          defaultMessage="Instant booking"
        />
      </Text>

      <div>
        {isInstantBookEligible ? (
          <RadioGroupField
            name="instantBookAvailability"
            label={intl.formatMessage({
              id: "hosting.activate_listings.instant_book_category",
              defaultMessage: "Who can book instantly?",
            })}
            note={
              <InstantBookLearnMorePopover
                testId={`activate-listing-form-${slug}-instant-book-learn-more-button`}
              />
            }
            required
            testId={`activate-listing-form-${slug}-instant-book-availability`}
          >
            <RadioGroupItem
              value="EVERYONE"
              id="instantBookEveryone"
              label={intl.formatMessage({
                id: "hosting.activate_listings.instant_book_everyone_optin",
                defaultMessage: "Everyone",
              })}
              note={intl.formatMessage({
                id: "hosting.activate_listings.instant_book_everyone_optin_note",
                defaultMessage:
                  "Guests who acknowledge and accept your host rules can book instantly.",
              })}
              data-testid={`activate-listing-form-${slug}-instant-book-everyone-radio`}
            />
            <RadioGroupItem
              value="NO_ONE"
              id="instantBookNoOne"
              label={intl.formatMessage({
                id: "hosting.activate_listings.no_one_option",
                defaultMessage: "No one",
              })}
              note={intl.formatMessage({
                id: "hosting.activate_listings.instant_book_noone_optin_note",
                defaultMessage:
                  "Booking requests will need to be manually accepted or declined.",
              })}
              data-testid={`activate-listing-form-${slug}-instant-book-noone-radio`}
            />
          </RadioGroupField>
        ) : (
          <>
            <Text
              typeface="body-1"
              element="p"
              className="tw-text-gray-600 tw-mb-8 -tw-mt-16"
              testId={`activate-listing-form-${slug}-instant-book-request-text`}
            >
              <FormattedMessage
                id="hosting.activate_listings.instant_book_request_text"
                defaultMessage="When on, booking requests will be accepted automatically. When off, you will need to manually accept all booking requests."
              />
            </Text>
            <div className="tw-mb-16">
              <InstantBookLearnMorePopover
                testId={`activate-listing-form-${slug}-instant-book-learn-more-button`}
              />
            </div>
            <CheckboxField
              name="instantBookRequested"
              description={intl.formatMessage({
                id: "hosting.activate_listings.instant_book_request_label",
                defaultMessage: "Request access",
              })}
              note={intl.formatMessage({
                id: "hosting.activate_listings.instant_book_request_label_note",
                defaultMessage:
                  "If requested, someone from our team will help you activate instant book.",
              })}
              testId={`activate-listing-form-${slug}-instant-book-requested-checkbox`}
            />
          </>
        )}

        <Text
          typeface="body-1-long"
          element="p"
          className="tw-bg-yellow-200 tw-text-yellow-900 tw-p-8 tw-mt-16"
          testId={`activate-listing-form-${slug}-instant-book-benefit`}
        >
          <FormattedMessage
            id="hosting.activate_listings.instant_book_benefit"
            defaultMessage="{useType} listings get up to 2x more bookings when guests can book instantly"
            values={{
              useType: intl.formatMessage(
                UPSELL_LISTING_NAME_BY_USE_TYPE[useType],
              ),
            }}
          />
        </Text>
      </div>
    </div>
  );
};

export { InstantBookingFields };
